import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, X } from '@phosphor-icons/react';
import { libraryImages } from '../assets/imageLibrary';

/**
 * Signature Services — editorial, magazine-style layout for the firm's
 * highest-value specialised engagements.
 *
 * Design principles (intentionally distinct from the main ServicesSection):
 *   • Two-column split cards: dominant image on one side, calm typography
 *     on the other. Mirrors the reading rhythm of a premium editorial page.
 *   • Serif numeric accents ("01"–"05") in a bronze tone establish
 *     sequence + prestige without a badge or label.
 *   • Zero body-bullet clutter on the card — a single one-line teaser only.
 *   • Full details open in an elegant modal (same interaction pattern the
 *     user knows from the main services section, but visually reserved).
 */

const SIGNATURE_SERVICES = [
  {
    id: 'mystery-parent',
    numeral: '01',
    title: 'ولي الأمر الخفي',
    titleEn: 'Mystery Parent',
    tagline: 'كشف الفجوات في رحلة ولي الأمر برؤية مهنية مستقلة.',
    description:
      'خدمة متخصصة تكشف الفجوات غير المرئية في رحلة ولي الأمر، وتقدم رؤى مهنية تساعد المنشآت على تحسين تجربة الأسرة وتعزيز جودة الخدمات المقدمة.',
    image: libraryImages.premiumMysteryParent,
  },
  {
    id: 'preopening-readiness',
    numeral: '02',
    title: 'مراجعة جاهزية الافتتاح',
    titleEn: 'Pre-opening Readiness Review',
    tagline: 'قياس جاهزية المنشأة قبل التشغيل لضمان بداية قوية.',
    description:
      'مراجعة احترافية لقياس جاهزية المنشأة قبل التشغيل، وتحديد فرص التطوير لضمان بداية قوية وتجربة تشغيل عالية الجودة منذ اليوم الأول.',
    image: libraryImages.premiumPreopeningReadiness,
  },
  {
    id: 'strategic-advisory',
    numeral: '03',
    title: 'الشراكة الاستشارية',
    titleEn: 'Strategic Advisory Partnership',
    tagline: 'شراكة استشارية مستمرة تدعم القيادات وصنّاع القرار.',
    description:
      'شراكة استشارية مستمرة تقدم دعماً مهنياً للقيادات وصناع القرار، وتسهم في تعزيز جودة الأداء ودعم التطوير المؤسسي وفق احتياجات المنشأة.',
    image: libraryImages.premiumStrategicAdvisory,
  },
];

const PremiumServicesSection = () => {
  const [openId, setOpenId] = useState(null);
  const open = openId ? SIGNATURE_SERVICES.find((s) => s.id === openId) : null;

  return (
    <section
      id="premium-services"
      data-testid="signature-services-section"
      className="py-24 sm:py-28 bg-[#F5F2EC]"
      dir="rtl"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header — restrained, editorial */}
        <motion.div
          className="max-w-2xl mx-auto text-center mb-16 sm:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-[10px] sm:text-[11px] font-medium tracking-[0.32em] text-[#8B6F3E] uppercase mb-6">
            Advanced Solutions
          </span>
          <h2 className="font-['Noto_Kufi_Arabic'] font-extrabold text-[#1F3D2B] text-[28px] sm:text-[36px] md:text-[44px] leading-[1.3] tracking-[-0.01em] mb-6">
            الحلول المتقدمة
          </h2>
          <div className="w-12 h-px bg-[#B08A50] mx-auto mb-6" />
          <p className="text-[#3D512D] text-[15px] sm:text-base leading-8 max-w-xl mx-auto">
            حلول استشارية متقدمة صُممت لمعالجة التحديات الأكثر تعقيداً، وتقديم
            رؤى أعمق ودعم نوعي للمنشآت العاملة في مجال الطفولة المبكرة.
          </p>
        </motion.div>

        {/* Editorial split cards — 2 per row on desktop, 1 on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {SIGNATURE_SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`signature-card-${s.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.55 }}
              className="group bg-white rounded-[24px] border border-[#E6E0D6] overflow-hidden flex flex-col sm:flex-row shadow-[0_1px_2px_rgba(31,61,43,0.04)] hover:shadow-[0_12px_32px_-12px_rgba(31,61,43,0.18)] hover:-translate-y-0.5 transition-all duration-500"
            >
              {/* Image — dominant visual, ~50% on md+ */}
              <div className="relative sm:w-[46%] sm:min-w-[46%] aspect-[4/3] sm:aspect-auto overflow-hidden bg-[#EEE8DC]">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement.style.background =
                      'linear-gradient(135deg,#E8E4DA 0%,#D9D3C5 100%)';
                  }}
                />
                {/* subtle warm overlay to bind imagery to palette */}
                <div className="absolute inset-0 bg-gradient-to-l from-[#1F3D2B]/0 via-transparent to-[#1F3D2B]/0 pointer-events-none" />
              </div>

              {/* Content — generous whitespace, minimal text */}
              <div className="flex-1 p-7 sm:p-8 md:p-9 flex flex-col justify-between min-h-[240px]">
                <div>
                  {/* Serif numeral accent — bronze */}
                  <div className="flex items-baseline gap-3 mb-5">
                    <span
                      className="font-['Playfair_Display','Cormorant_Garamond','Noto_Serif',serif] text-[#B08A50] text-[26px] leading-none tracking-wide"
                      dir="ltr"
                    >
                      {s.numeral}
                    </span>
                    <span className="h-px flex-1 bg-[#E4D9C4]" />
                  </div>

                  <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-[22px] sm:text-[24px] leading-tight tracking-tight mb-1.5">
                    {s.title}
                  </h3>
                  <p
                    className="text-[12px] sm:text-[13px] text-[#8B6F3E] tracking-[0.14em] uppercase mb-5"
                    dir="ltr"
                  >
                    {s.titleEn}
                  </p>

                  <p className="text-[14px] sm:text-[15px] text-[#4A5D4E] leading-[1.85]">
                    {s.tagline}
                  </p>
                </div>

                {/* Action row — bronze underline for the discover CTA */}
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
                  <button
                    type="button"
                    data-testid={`signature-discover-btn-${s.id}`}
                    onClick={() => setOpenId(s.id)}
                    className="inline-flex items-center gap-2 text-[14px] font-bold text-[#1F3D2B] border-b border-[#B08A50] hover:border-[#1F3D2B] pb-1 transition-colors"
                  >
                    اكتشف الخدمة
                    <ArrowLeft size={14} className="rtl-flip" />
                  </button>
                  <Link
                    to={`/quotation?service=${s.id}`}
                    data-testid={`signature-quote-btn-${s.id}`}
                    className="inline-flex items-center gap-2 text-[13px] text-[#7A866E] hover:text-[#1F3D2B] transition-colors"
                  >
                    اطلب عرض سعر
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <p className="text-center text-xs sm:text-sm text-[#8B6F3E] mt-14 tracking-wide">
          جميع الحلول المتقدمة تُسعَّر حسب نطاق العمل ومتطلبات المنشأة
        </p>
      </div>

      {/* Details modal — reserved, monochrome */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#1F3D2B]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setOpenId(null)}
            data-testid="signature-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.35 }}
              className="bg-white rounded-[24px] max-w-xl w-full my-8 relative max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
              dir="rtl"
              data-testid="signature-details-modal"
            >
              {/* Image header */}
              <div className="relative h-56 sm:h-64 overflow-hidden bg-[#EEE8DC] shrink-0">
                <img
                  src={open.image}
                  alt={open.title}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setOpenId(null)}
                  aria-label="إغلاق"
                  data-testid="signature-modal-close"
                  className="absolute top-4 left-4 w-9 h-9 rounded-full bg-white/95 hover:bg-white flex items-center justify-center text-[#1F3D2B] transition-colors shadow-sm"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-7 sm:p-9 overflow-y-auto">
                <div className="flex items-baseline gap-3 mb-4">
                  <span
                    className="font-['Playfair_Display','Cormorant_Garamond','Noto_Serif',serif] text-[#B08A50] text-2xl leading-none"
                    dir="ltr"
                  >
                    {open.numeral}
                  </span>
                  <span className="h-px flex-1 bg-[#E4D9C4]" />
                </div>
                <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-[24px] sm:text-[28px] leading-tight tracking-tight mb-1.5">
                  {open.title}
                </h3>
                <p
                  className="text-[12px] sm:text-[13px] text-[#8B6F3E] tracking-[0.14em] uppercase mb-6"
                  dir="ltr"
                >
                  {open.titleEn}
                </p>
                <p className="text-[#3D512D] leading-9 text-[15px] mb-7">
                  {open.description}
                </p>

                <div className="pt-6 border-t border-[#E6E0D6] flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/quotation?service=${open.id}`}
                    data-testid="signature-modal-quote-btn"
                    onClick={() => setOpenId(null)}
                    className="flex-1 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                  >
                    اطلب عرض سعر
                    <ArrowLeft size={16} className="rtl-flip" />
                  </Link>
                  <Link
                    to="/booking"
                    data-testid="signature-modal-booking-btn"
                    onClick={() => setOpenId(null)}
                    className="flex-1 border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                  >
                    احجز جلسة استشارية
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PremiumServicesSection;
